import { fetchAccountsPromise, updateAccountPromise } from 'app/app/(client)/db/db'
import { OpportunityCustomer, OpportunitySfId, SyncStatus } from 'app/types/types'
import { setCurrentSync } from '../reduxSlices/current-sync-slice'
import { getSFData, getSFDataWithoutConfig } from 'app/services/client/client-api'
import { t } from 'i18next'
import * as R from 'ramda'
import { checkIfContractInactive } from 'app/app/common/common-utils'
import { RootState } from '../../../redux/Store'
import { wrapCreateAsyncThunk } from 'app/app/(client)/utils/async-handler'

export const fetchNewData = async (getState: () => RootState, dispatch: any) => {
  await fetchAccountsPromise()
    .then(async accounts => getData(accounts, getState, dispatch))
    .catch(err => console.log('error fetching data from IndexedDB', err))
}

const getData = async (accounts: OpportunityCustomer[], getState: () => any, dispatch: any) => {
  const updateOppSync = (oppId: string, error: string, syncedWithSF?: boolean) =>
    updateSync(oppId, error, syncedWithSF, dispatch, getState)

  await accounts.reduce(async (previousPromise, account) => {
    await previousPromise

    const oppList = getState().currentSync.data || {}

    const unsyncedOpportunities = account.opportunities.filter(
      opportunity => oppList?.[opportunity.id] && !oppList[opportunity.id].syncedWithSF
    )

    if (unsyncedOpportunities.length === 0) {
      return
    }

    const updatedSync = unsyncedOpportunities.reduce(
      (syncData, opportunity) =>
        updateCurrentSync(opportunity.id, { syncedWithSF: false, isSyncing: true, syncError: '' })(syncData),
      oppList
    )

    dispatch(setCurrentSync(updatedSync))

    const oppIds = unsyncedOpportunities.map(opp => opp.id)
    await syncAccount(oppIds, updateOppSync, getState)
  }, Promise.resolve())
}

const syncAccount = async (
  opportunityIds: string[],
  syncFunction: (oppId: string, error: string, syncedWithSF?: boolean) => void,
  getState: () => any
) => {
  const frontlineConfig = getState().configdata.data?.frontLineConfig
  if (!frontlineConfig) {
    return
  }
  const enabledEquipment = frontlineConfig.enabledEquipment
  const manufacturers = frontlineConfig.manufacturers
  const quickTender = frontlineConfig.quickTender
  const peopleflowTechnicalPlatforms = frontlineConfig.peopleflowTechnicalPlatforms
  const cutoverDates = frontlineConfig.cutoverDates

  const result = await getSFDataWithoutConfig({
    opportunityIds,
    enabledEquipment,
    manufacturers,
    quickTender,
    peopleflowTechnicalPlatforms,
    cutoverDates
  })
  updateIndexDB(result, opportunityIds, syncFunction)
}

const updateCurrentSync =
  (oppId: string, value: SyncStatus) =>
  (status: Record<string, SyncStatus>): Record<string, SyncStatus> => ({ ...status, [oppId]: value })

const updateIndexDB = (
  opportunityCustomer: OpportunityCustomer,
  opportunityIds: string[],
  syncFunction: (oppId: string, error: string, syncedWithSF?: boolean) => void
) => {
  if (R.isEmpty(opportunityCustomer) || R.isNil(opportunityCustomer)) {
    opportunityIds.forEach(id => {
      syncFunction(id, t('assessment.overview.initial.syncWithSalesForceError'))
    })
  } else {
    const customer = R.dissoc('opportunities', opportunityCustomer)

    const opportunities = opportunityCustomer.opportunities || []
    const oppIds = opportunities.map(opp => opp.id)
    const notFetchedOpportunities = opportunityIds.filter(id => !oppIds.includes(id))

    notFetchedOpportunities.forEach(id => {
      syncFunction(id, t('customer.invalidVASOpportunity'))
    })

    opportunities.forEach(opp =>
      updateAccountPromise(customer, { ...opp, syncedWithSF: true, isSyncing: false })
        .then(_ =>
          checkIfContractInactive(opp)
            ? syncFunction(opp.id, t('assessment.overview.initial.inactiveContract'))
            : syncFunction(opp.id, '', true)
        )
        .catch(err => {
          console.log(err)
          syncFunction(opp.id, t('assessment.overview.initial.syncWithSalesForceError'))
        })
    )
  }
}

const updateSync = async (
  id: string,
  syncError: string,
  syncedWithSF: boolean = false,
  dispatch: any,
  getState: () => any
) => {
  const currentSyncData = getState().currentSync.data || {}
  await dispatch(
    setCurrentSync(
      updateCurrentSync(id, {
        syncedWithSF,
        isSyncing: false,
        syncError
      })(currentSyncData)
    )
  )
}

export const syncOpportunity = wrapCreateAsyncThunk<void, OpportunitySfId>(
  'currentSync/syncOpportunity',
  async (opportunityId: OpportunitySfId, thunkAPI) => {
    const { dispatch, getState } = thunkAPI || {}
    const state = getState ? (getState() as RootState) : undefined
    const currentSyncData = (state as RootState | undefined)?.currentSync.data || {}

    dispatch(
      setCurrentSync(
        updateCurrentSync(opportunityId, { syncedWithSF: false, isSyncing: true, syncError: '' })(currentSyncData)
      )
    )

    const result: OpportunityCustomer = await getSFData(opportunityId)

    const updateOppSync = (oppId: string, error: string, syncedWithSF?: boolean) =>
      updateSync(oppId, error, syncedWithSF, dispatch, getState)

    updateIndexDB(result, [opportunityId], updateOppSync)
  }
)
